/**
 * Estilos y constructores de celda compartidos por los dos reportes de nómina.
 *
 * Se apoya en `Components/ExcelEstilos.js` para lo que ya es estándar del
 * proyecto (el formato Contabilidad y el borde delgado) y agrega lo propio de
 * estas plantillas: los banners, la fila de "CAMPO OBLIGATORIO" y la de
 * especificaciones de llenado.
 *
 * Reglas de xlsx-js-style que aplican aquí:
 *   · Toda celda numérica necesita `t: "n"` explícito. Sin él la librería la
 *     escribe como texto y Excel nunca aplica el numFmt (sale el triángulo
 *     verde de "número guardado como texto").
 *   · Una celda de dinero que se pinta vacía conserva el estilo con su numFmt,
 *     para que si el usuario teclea un número encima ya salga en Contabilidad.
 *   · `!freeze` / `!panes` no hacen nada (son de SheetJS Pro) y `!autofilter`
 *     se traga las filas de Totales: ninguno de los dos se usa.
 */

import { FORMATO_CONTABILIDAD, border } from "../../../Components/ExcelEstilos";

export { FORMATO_CONTABILIDAD, border };

const AZUL_BANNER = "BDD7EE";
const GRIS_HEADER = "D9D9D9";
const NARANJA_TOTAL = "F8CBAD";
const AMARILLO_GUIA = "FFF2CC";

export const bannerStyle = {
  font: { bold: true, sz: 12 },
  fill: { fgColor: { rgb: AZUL_BANNER } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border,
};

export const headerStyle = {
  font: { bold: true, sz: 10 },
  fill: { fgColor: { rgb: GRIS_HEADER } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border,
};

export const obligatorioStyle = {
  font: { bold: true, sz: 8, color: { rgb: "C00000" } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border,
};

export const guiaStyle = {
  font: { sz: 8, italic: true },
  fill: { fgColor: { rgb: AMARILLO_GUIA } },
  alignment: { horizontal: "left", vertical: "top", wrapText: true },
  border,
};

export const textoStyle = { alignment: { vertical: "center" }, border };

export const centroStyle = {
  alignment: { horizontal: "center", vertical: "center" },
  border,
};

export const numeroStyle = {
  alignment: { horizontal: "right", vertical: "center" },
  border,
};

export const monedaStyle = {
  alignment: { horizontal: "right", vertical: "center" },
  numFmt: FORMATO_CONTABILIDAD,
  border,
};

export const totalTextoStyle = {
  font: { bold: true },
  fill: { fgColor: { rgb: NARANJA_TOTAL } },
  alignment: { horizontal: "center", vertical: "center" },
  border,
};

export const totalNumeroStyle = {
  font: { bold: true },
  fill: { fgColor: { rgb: NARANJA_TOTAL } },
  alignment: { horizontal: "right", vertical: "center" },
  border,
};

export const totalMonedaStyle = {
  ...totalNumeroStyle,
  numFmt: FORMATO_CONTABILIDAD,
};

// ── Constructores de celda ───────────────────────────────────────────────────

export const celda = (valor, estilo = textoStyle) => ({
  v: valor === undefined || valor === null ? "" : valor,
  s: estilo,
});

export const celdaNumero = (valor, estilo = numeroStyle) => {
  const numero = Number(valor);
  return valor !== "" && valor !== null && valor !== undefined && Number.isFinite(numero)
    ? { v: numero, t: "n", s: estilo }
    : { v: "", s: estilo };
};

// El estilo se conserva aunque la celda salga vacía: así, si alguien teclea un
// importe encima en Excel, ya aparece en formato Contabilidad.
export const celdaMoneda = (valor, estilo = monedaStyle) => celdaNumero(valor, estilo);

// Fila completa de un solo estilo, para banners y separadores.
export const filaDe = (largo, estilo, textos = {}) =>
  Array.from({ length: largo }, (_, i) => ({ v: textos[i] ?? "", s: estilo }));
